import { cn } from '@/utils'
import { SHAPE_GROUP_HOVER_FILL_CLASS } from '@/components/app/editor/selection-chrome/group-hover-fill'
import {
  FROZEN_OBJECT_CURSOR,
  LOCKED_OBJECT_CURSOR,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'

/** Interaction state that decides how the shape's body path looks and responds to the pointer. */
export interface ShapeBodyState {
  /** Shapes tool active, or the shape isn't independently selectable in the current scope. */
  isInShapeCreationMode: boolean
  /** Locked by another user (advisory lock). */
  remoteLocked: boolean
  /** Locked by this user (`shape.locked`). */
  locked: boolean
  isSelected: boolean
}

/** True when the body path is interactive at all (not in creation mode, not remote-locked). */
function isInteractive(state: Pick<ShapeBodyState, 'isInShapeCreationMode' | 'remoteLocked'>) {
  return !state.isInShapeCreationMode && !state.remoteLocked
}

/** Kind-7 groups use the group hover fill, unless the shape isn't interactive. */
export function shouldUseGroupHoverStyle(
  isGroup: boolean,
  hasGroupHoverStyle: boolean,
  state: Pick<ShapeBodyState, 'isInShapeCreationMode' | 'remoteLocked'>
): boolean {
  return isGroup && hasGroupHoverStyle && isInteractive(state)
}

/** Frozen-geometry cursor applies to an interactive, unlocked shape showing any frozen chrome. */
export function isFrozenGeometryCursor(
  state: Omit<ShapeBodyState, 'isSelected'>,
  showsFrozenChrome: boolean
): boolean {
  return isInteractive(state) && !state.locked && showsFrozenChrome
}

/** Inline `cursor`: the lock cursor wins over the frozen cursor; otherwise the class decides. */
export function shapeBodyCursor(
  state: Omit<ShapeBodyState, 'isSelected'>,
  frozenGeometryCursor: boolean
): string | undefined {
  if (isInteractive(state) && state.locked) {
    return LOCKED_OBJECT_CURSOR
  }

  return frozenGeometryCursor ? FROZEN_OBJECT_CURSOR : undefined
}

export function shapeBodyClassName(
  state: ShapeBodyState,
  options: {
    useGroupHoverStyle: boolean
    hasPhotoFill: boolean
    isTextFrame: boolean
    /** A color was just dragged onto or dropped on this shape; keep its fill visible. */
    suppressHoverTint?: boolean
  }
): string {
  const interactive = isInteractive(state)

  return cn(
    'transition-colors',
    state.remoteLocked && 'pointer-events-none',
    // Not for a text frame: a gray hover tint over a box someone's about to read/type into reads as
    // an unwanted "highlighted" state rather than a hover affordance.
    interactive &&
      !options.useGroupHoverStyle &&
      !options.hasPhotoFill &&
      !options.isTextFrame &&
      !options.suppressHoverTint &&
      'hover:fill-muted',
    options.useGroupHoverStyle && SHAPE_GROUP_HOVER_FILL_CLASS,
    interactive && !state.locked && (state.isSelected ? 'cursor-move' : 'cursor-default')
  )
}

export function shapeBodyPointerEvents(
  state: Pick<ShapeBodyState, 'isInShapeCreationMode' | 'remoteLocked'>
): 'none' | 'all' {
  return isInteractive(state) ? 'all' : 'none'
}
